import React from 'react';
import PropTypes from 'prop-types';

/**
 * NeumorphicCard - A modern neumorphic card component with soft shadows
 */
const NeumorphicCard = ({
  children,
  className = '',
  variant = 'light', // Changed default from 'dark' to 'light'
  intensity = 'medium',
  interactive = false,
  flat = false,
  inset = false,
  ...props
}) => {
  // Base styles - improved padding and border handling
  const baseStyles = 'rounded-xl transition-all duration-300 p-6 border';
  
  // Background color based on variant - enhanced for maximum visibility
  const bgStyles = {
    light: 'bg-white', // Pure white background
    dark: 'bg-gray-700',
    primary: 'bg-yellow-100',
    secondary: 'bg-pink-100'};
  
  // Border styles for each variant - enhanced for better visibility
  const borderStyles = {
    light: 'border-gray-200', // Lighter border for white background
    dark: 'border-white/50',
    primary: 'border-yellow-300',
    secondary: 'border-pink-300'};
  
  // Text color based on variant - improved readability
  const textStyles = {
    light: 'text-black', // Pure black text for maximum contrast
    dark: 'text-white',
    primary: 'text-yellow-800',
    secondary: 'text-pink-800'};
  
  // Shadow intensity - enhanced for maximum visibility
  const shadowIntensity = {
    light: {
      low: inset 
        ? 'inset 4px 4px 8px rgba(0, 0, 0, 0.2), inset -4px -4px 8px rgba(255, 255, 255, 1)' 
        : '4px 4px 8px rgba(0, 0, 0, 0.2), -4px -4px 8px rgba(255, 255, 255, 1)',
      medium: inset 
        ? 'inset 6px 6px 12px rgba(0, 0, 0, 0.2), inset -6px -6px 12px rgba(255, 255, 255, 1)' 
        : '6px 6px 12px rgba(0, 0, 0, 0.2), -6px -6px 12px rgba(255, 255, 255, 1)',
      high: inset 
        ? 'inset 10px 10px 20px rgba(0, 0, 0, 0.2), inset -10px -10px 20px rgba(255, 255, 255, 1)' 
        : '10px 10px 20px rgba(0, 0, 0, 0.2), -10px -10px 20px rgba(255, 255, 255, 1)'},
    dark: {
      low: inset 
        ? 'inset 4px 4px 8px rgba(0, 0, 0, 0.8), inset -4px -4px 8px rgba(255, 255, 255, 0.2)' 
        : '4px 4px 8px rgba(0, 0, 0, 0.8), -4px -4px 8px rgba(100, 100, 150, 0.3)',
      medium: inset 
        ? 'inset 6px 6px 12px rgba(0, 0, 0, 0.8), inset -6px -6px 12px rgba(255, 255, 255, 0.2)' 
        : '6px 6px 12px rgba(0, 0, 0, 0.8), -6px -6px 12px rgba(100, 100, 150, 0.3)',
      high: inset 
        ? 'inset 10px 10px 20px rgba(0, 0, 0, 0.8), inset -10px -10px 20px rgba(255, 255, 255, 0.2)' 
        : '10px 10px 20px rgba(0, 0, 0, 0.8), -10px -10px 20px rgba(100, 100, 150, 0.3)'},
    primary: {
      low: inset 
        ? 'inset 4px 4px 8px rgba(79, 70, 229, 0.3), inset -4px -4px 8px rgba(255, 255, 255, 1)' 
        : '4px 4px 8px rgba(79, 70, 229, 0.3), -4px -4px 8px rgba(255, 255, 255, 1)',
      medium: inset 
        ? 'inset 6px 6px 12px rgba(79, 70, 229, 0.3), inset -6px -6px 12px rgba(255, 255, 255, 1)' 
        : '6px 6px 12px rgba(79, 70, 229, 0.3), -6px -6px 12px rgba(255, 255, 255, 1)',
      high: inset 
        ? 'inset 10px 10px 20px rgba(79, 70, 229, 0.3), inset -10px -10px 20px rgba(255, 255, 255, 1)' 
        : '10px 10px 20px rgba(79, 70, 229, 0.3), -10px -10px 20px rgba(255, 255, 255, 1)'},
    secondary: {
      low: inset 
        ? 'inset 4px 4px 8px rgba(236, 72, 153, 0.3), inset -4px -4px 8px rgba(255, 255, 255, 1)' 
        : '4px 4px 8px rgba(236, 72, 153, 0.3), -4px -4px 8px rgba(255, 255, 255, 1)',
      medium: inset 
        ? 'inset 6px 6px 12px rgba(236, 72, 153, 0.3), inset -6px -6px 12px rgba(255, 255, 255, 1)' 
        : '6px 6px 12px rgba(236, 72, 153, 0.3), -6px -6px 12px rgba(255, 255, 255, 1)',
      high: inset 
        ? 'inset 10px 10px 20px rgba(236, 72, 153, 0.3), inset -10px -10px 20px rgba(255, 255, 255, 1)' 
        : '10px 10px 20px rgba(236, 72, 153, 0.3), -10px -10px 20px rgba(255, 255, 255, 1)'}};
  
  // Interactive styles - enhanced for better feedback
  const interactiveStyles = interactive 
    ? 'cursor-pointer hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] active:shadow-inner transition-all duration-300' 
    : '';
  
  // Flat styles (no shadow)
  const flatStyles = flat ? 'shadow-none' : '';

  // Add enhanced inner glow and gradient for better visibility
  const getBackgroundGradient = () => {
    if (flat) return {};
    
    switch(variant) {
      case 'light':
        return { background: 'linear-gradient(145deg, #ffffff, #f8f9fa)' }; // Subtle white gradient
      case 'dark':
        return { background: 'linear-gradient(145deg, #3a4a63, #536480)' }; // Much lighter gradient for better visibility
      case 'primary':
        return { background: 'linear-gradient(145deg, #c7d2fe, #e0e7ff)' }; // Indigo gradient
      case 'secondary':
        return { background: 'linear-gradient(145deg, #fbcfe8, #fce7f3)' }; // Pink gradient
      default:
        return {};
    }
  };
  
  const innerGlowStyle = {
    boxShadow: flat ? 'none' : shadowIntensity[variant][intensity],
    ...getBackgroundGradient()
  };
  
  return (
    <div
      className={`${baseStyles} ${bgStyles[variant]} ${textStyles[variant]} ${borderStyles[variant]} ${interactiveStyles} ${flatStyles} ${className}`}
      style={innerGlowStyle}
      data-component-name="NeumorphicCard"
      {...props}
    >
      {children}
    </div>
  );
};

NeumorphicCard.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['light', 'dark', 'primary', 'secondary']),
  intensity: PropTypes.oneOf(['low', 'medium', 'high']),
  interactive: PropTypes.bool,
  flat: PropTypes.bool,
  inset: PropTypes.bool};

export default NeumorphicCard;
